import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Headline from '../Components/Elements/Headline'
import Text from '../Components/Elements/Text'
import Button from '../Components/Formik/Button'
import Guide from '../Components/Guide/Guide'
import CardAccounting from '../Components/Sheets/Accounting/Card'
import CreateAccounting from '../Components/Sheets/Accounting/CreateAccounting'
import CardBudget from '../Components/Sheets/Budget/Card'
import CreateBudgetModal from '../Components/Sheets/Budget/CreateBudgetModal/CreateBudgetModal'
import { useAccountings } from '../Components/Sheets/lib/Hooks/Accounting'
import { useBudgets } from '../Components/Sheets/lib/Hooks/Budget'
import { Accounting, Budget, Sheet, Spreadsheet } from '../lib/Types'

import {
  MdArrowDropDown,
  MdArrowRight,
  MdOutlineStarPurple500,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { SheetType } from '../Components/Sheets/lib/Enums/SheetType'
import {
  useCreateSpreadsheet,
  useSpreadsheets,
} from '../Components/Sheets/lib/Hooks/Spreadsheet'
import SpreadsheetCard from '../Components/Sheets/Spreadsheet/SpreadsheetCard'
export default function DashboardPage() {
  const { t } = useTranslation('dashboard')
  const [showArchived, toggleArchived] = useState(false)
  const { data: budgets } = useBudgets()
  const { data: accountings } = useAccountings()
  const { data: spreadsheets } = useSpreadsheets()
  const [showCreateAccounting, toggleShowCreateAccounting] = useState(false)
  const { mutateAsync: createSpreadsheet } = useCreateSpreadsheet()

  const favorites = [
    ...budgets!.filter((item) => item.is_favorite),
    ...accountings!.filter((item) => item.is_favorite),
    ...spreadsheets!.filter((item) => item.is_favorite),
  ]
  const archived = [
    ...budgets!.filter((item) => item.is_archived),
    ...accountings!.filter((item) => item.is_archived),
    ...spreadsheets!.filter((item) => item.is_archived),
  ]
  const navigate = useNavigate()

  return (
    <>
      <Guide />
      {favorites.length > 0 && (
        <div className=' border-4  border-gray-50 px-4 py-2'>
          <Headline className='flex'>
            <MdOutlineStarPurple500
              size='1.75rem'
              className='mr-1'
              fill='#FFC300'
            />
            {t('dashboard:favorites')}
          </Headline>
          <DashboardColumns<Sheet>
            items={favorites}
            render={(item: Sheet) => (
              <div key={item.id}>
                {item.sheet_type === SheetType.BUDGET && (
                  <CardBudget budget={item as Budget} key={item.id} />
                )}
                {item.sheet_type === SheetType.ACCOUNTING && (
                  <CardAccounting
                    accounting={item as Accounting}
                    key={item.id}
                  />
                )}{' '}
                {item.sheet_type === SheetType.SPREADSHEET && (
                  <SpreadsheetCard
                    spreadsheet={item as Spreadsheet}
                    key={item.id}
                  />
                )}
              </div>
            )}
          />
        </div>
      )}

      <Headline>
        <div className='flex flex-col md:flex-row justify-between md:items-center mt-12'>
          {t('dashboard:budget.heading')}
          <CreateBudgetModal />
        </div>
      </Headline>
      <DashboardColumns<Budget>
        items={budgets!.filter((budget) => !budget.is_archived)}
        emptyText={t('dashboard:budget.empty')}
        render={(item: Budget) => <CardBudget budget={item} key={item.id} />}
      />
      <Headline className='mt-12'>
        <div className='flex flex-col md:flex-row justify-between md:items-center'>
          {t('dashboard:accounting.heading')}
          <Button
            onClick={() => toggleShowCreateAccounting(!showCreateAccounting)}
            className='ml-0 md:ml-2 mb-4 md:mb-0'
          >
            {t('dashboard:accounting.create')}
          </Button>
        </div>
      </Headline>
      <DashboardColumns<Accounting>
        items={accountings!.filter((accounting) => !accounting.is_archived)}
        emptyText={t('dashboard:accounting.empty')}
        render={(item: Accounting) => (
          <CardAccounting accounting={item} key={item.id} />
        )}
      />
      <Headline className='mt-12'>
        <div className='flex flex-col md:flex-row justify-between md:items-center'>
          {t('dashboard:spreadsheet.heading')}
          <Button
            onClick={async () => {
              const spreadsheet = await createSpreadsheet()
              navigate(`/spreadsheet/${spreadsheet.id}/`)
            }}
            className='ml-0 md:ml-2 mb-4 md:mb-0'
          >
            {t('dashboard:spreadsheet.create')}
          </Button>
        </div>
      </Headline>
      <DashboardColumns<Spreadsheet>
        items={spreadsheets!.filter((spreadsheet) => !spreadsheet.is_archived)}
        emptyText={t('dashboard:spreadsheet.empty')}
        render={(item: Spreadsheet) => (
          <SpreadsheetCard spreadsheet={item} key={item.id} />
        )}
      />
      <div className='mt-12 mb-24'>
        <Headline>
          <div
            className='flex flex-col md:flex-row  md:items-center cursor-pointer'
            onClick={() => toggleArchived(!showArchived)}
          >
            <span className='pr-2'>{t('dashboard:archived.heading')}</span>
            {showArchived && <MdArrowDropDown size='1.75rem' />}
            {!showArchived && <MdArrowRight size='1.75rem' />}
          </div>
        </Headline>
        {showArchived && (
          <DashboardColumns<Sheet>
            items={archived}
            emptyText={t('dashboard:archived.empty')}
            render={(item: Sheet) => (
              <div key={item.id}>
                {item.sheet_type === SheetType.BUDGET && (
                  <CardBudget budget={item as Budget} key={item.id} />
                )}
                {item.sheet_type === SheetType.ACCOUNTING && (
                  <CardAccounting
                    accounting={item as Accounting}
                    key={item.id}
                  />
                )}{' '}
                {item.sheet_type === SheetType.SPREADSHEET && (
                  <SpreadsheetCard
                    spreadsheet={item as Spreadsheet}
                    key={item.id}
                  />
                )}
              </div>
            )}
          />
        )}
      </div>
      <CreateAccounting
        showModal={showCreateAccounting}
        toggleModal={toggleShowCreateAccounting}
      />
    </>
  )
}

interface DashboardColumnsProps<T> {
  items: T[]
  emptyText?: string
  render: (item: T) => ReactNode
}

function DashboardColumns<T>({
  items,
  emptyText,
  render,
}: DashboardColumnsProps<T>) {
  const column1 = items!.filter((_, index) => index % 3 === 0)
  const column2 = items!.filter((_, index) => index % 3 === 1)
  const column3 = items!.filter((_, index) => index % 3 === 2)

  return (
    <div className='flex flex-wrap md:flex-nowrap justify-between mb-5'>
      <div className='w-full md:w-1/3 mr-0 md:mr-2'>
        {items!.length === 0 && <Text size='base'>{emptyText}</Text>}
        {column1.map((item: T, index) => render(item))}
      </div>
      <div className='w-full md:w-1/3 mx-0 md:mx-2'>
        {column2.map((item, index) => render(item))}
      </div>
      <div className='w-full md:w-1/3 ml-0 md:ml-2'>
        {column3.map((item, index) => render(item))}
      </div>
    </div>
  )
}
